import { supabase } from './supabase';
import type { Json } from '../types/database.types';

// Frontera con el modelo de tutor (migs 196-198). Nació con casts porque los
// tipos se generan contra la BD real y esas migraciones no estaban aplicadas;
// ya lo están y los tipos regenerados (d2ec87e) traen `guardians`,
// `guardian_students` y las cuatro RPC, así que los casts se han retirado.
//
// NO se comparte fichero con la app a propósito: cada módulo tiene su cliente
// de Supabase y sus tipos generados.

// Las RPC devuelven `jsonb` → `Json`, un union con escalares y arrays.
// Estrecharlo a objeto es la lectura correcta, no un cast.
const asRecord = (data: Json | null): Record<string, Json | undefined> =>
  data !== null && typeof data === 'object' && !Array.isArray(data) ? data : {};

// Un embed *-a-uno llega como objeto, pero el generador lo tipa como array
// porque la FK no es `isOneToOne`. Normalizamos en la frontera en vez de
// castear en cada uso.
const one = <T>(v: T | T[] | null | undefined): T | null =>
  Array.isArray(v) ? (v[0] ?? null) : (v ?? null);

export interface Guardian {
  id: string;
  profile_id: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
}

export interface GuardianLink {
  id: string;
  guardian_id: string;
  student_id: string;
  relationship: string | null;
  is_primary: boolean;
  revoked_at: string | null;
}

/** Un alumno con su familia resuelta: quién le tutela y con quién comparte tutor. */
export interface FamilyOf {
  guardians: { id: string; name: string; email: string | null; hasAccount: boolean }[];
  siblings: { id: string; name: string }[];
}

// ============================================================
// EL CORREO TÉCNICO NO SE ENSEÑA
// ============================================================
// Una cuenta de menor sin buzón propio lleva `<uuid>@no-login.vertexperformance.es`
// en `auth.users`. Es un marcador interno —`auth.users.email` es NOT NULL y
// único, así que hace falta ALGO—, no una dirección a la que se pueda escribir.
//
// Hasta la 297 casi no se veía: esas cuentas nacían por el alta unificada (mig
// 201), que deja `profiles.email` a NULL y guarda el marcador SOLO en
// `auth.users`. Lo dice `set-child-email/handler.ts`: «en una cuenta técnica
// `profiles.email` es NULL». La 297 y la 298 rompieron esa convención y
// escribieron el marcador también en `profiles`, así que 32 alumnos lo llevan
// hoy en la tabla que lee el panel.
//
// Se arregla en los dos lados: el dato, en su migración; y aquí, porque una
// pantalla que enseña el correo de un alumno nunca debe enseñar este.

/** `true` si ese correo es el marcador interno de una cuenta sin buzón propio. */
export function esCorreoTecnico(email: string | null | undefined): boolean {
  return (email ?? '').toLowerCase().includes('@no-login.');
}

/**
 * El correo con el que el club se comunica con un alumno.
 *
 * El del TUTOR manda cuando lo hay: es la decisión del modelo de tutor (migs
 * 196-209), donde quien recibe las comunicaciones de un menor es el adulto que
 * lo tutela, y no depende de si el niño tiene buzón propio.
 *
 * Devuelve `null` cuando no hay ninguno al que escribir — que es un dato, no un
 * hueco: significa que el club no tiene forma de contactar con esa familia.
 */
export function correoDeContacto(
  alumnoEmail: string | null | undefined,
  familia: FamilyOf | undefined
): { email: string; esDelTutor: boolean } | null {
  const tutor = familia?.guardians.find((g) => g.email && !esCorreoTecnico(g.email));
  if (tutor?.email) return { email: tutor.email, esDelTutor: true };
  const propio = (alumnoEmail ?? '').trim();
  if (propio && !esCorreoTecnico(propio)) return { email: propio, esDelTutor: false };
  return null;
}

export interface RpcOutcome {
  ok: boolean;
  message?: string;
}

/**
 * Las 5 formas que puede tener una familia según `family_shapes()` (mig 208).
 * La clasificación cuelga de `parent_email` y NO de `is_minor`, que es una foto
 * del día del alta que nadie refresca.
 */
export type FamilyShape =
  | 'ya_vinculado'
  | 'cuenta_es_del_hijo'
  | 'tutor_sin_cuenta'
  | 'solo_vincular'
  | 'conflicto';

export interface FamilyShapeRow {
  student_id: string;
  student_name: string;
  student_email: string | null;
  parent_name: string | null;
  parent_email: string | null;
  parent_phone: string | null;
  shape: FamilyShape;
  /** Rol de la cuenta que ocupa el email del tutor. Solo en `conflicto`. */
  blocker_role: string | null;
}

// ============================================================
// SI UN TUTOR HA LLEGADO A ENTRAR, Y CUÁNDO
// ============================================================
// Sale de `admin_tutor_access()` (mig 301) y no de una lectura normal porque
// el dato vive en `auth.users`, que el cliente NO puede leer con la anon key.
// Es la misma razón por la que /directores tiene `admin_list_directors`.
//
// ⚠️ Las fechas NO son las de la fila del tutor. Las migs 297/298 crearon
//    cuentas nuevas y les movieron el hash, así que esas filas dicen «alta
//    hoy, nunca ha entrado» de 32 familias que llevan semanas entrando. La
//    función devuelve las de la familia; el porqué está en su cabecera.

export interface TutorAccess {
  profile_id: string;
  /** Desde cuándo está esta familia en el club. */
  alta_familia: string | null;
  /** Cuándo se creó la FILA del tutor. Posterior al alta = la creó una migración. */
  cuenta_creada: string;
  /** El último acceso de verdad. `null` = no ha entrado nunca nadie. */
  ultimo_acceso: string | null;
  /** El del tutor a secas, sin heredar. Distingue «entró ya como tutor». */
  acceso_como_tutor: string | null;
  /** Sin esto GoTrue le NIEGA el login. No es cosmético. */
  email_confirmado: boolean;
  tiene_contrasena: boolean;
}

/**
 * El error se PROPAGA en vez de devolver un mapa vacío: sin él la pantalla
 * pintaría «nunca ha entrado» en todas las filas, que es una afirmación, y la
 * contraria a la verdad. Mismo criterio que `tutoresInvisibles`.
 */
export async function tutorAccess(): Promise<{
  accesos: Map<string, TutorAccess>;
  error: string | null;
}> {
  const { data, error } = await supabase.rpc('admin_tutor_access');
  if (error) return { accesos: new Map(), error: error.message || 'Error desconocido' };
  const out = new Map<string, TutorAccess>();
  for (const r of (data ?? []) as TutorAccess[]) out.set(r.profile_id, r);
  return { accesos: out, error: null };
}

/**
 * Reenvía a un tutor el enlace para definir su contraseña.
 *
 * ⚠️ En /directores esto pasa por la EF `reinvite-director`, y aquí NO, a
 *    propósito. Allí la EF existe porque hace DOS cosas: reactivar la cuenta
 *    —un RPC root-only que el cliente no puede llamar— y reenviar el enlace.
 *    La mitad que necesitaba privilegios es la reactivación, y en Familias esa
 *    ya tiene su propio botón (`setCuentaTutorActiva`).
 *
 *    Lo único que quedaría dentro de una EF sería `resetPasswordForEmail`, que
 *    es exactamente la misma llamada que hace LoginPage en «¿Olvidaste tu
 *    contraseña?» con la anon key, y la que acaba haciendo `invite-tutor`.
 *    Envolverla no añade ningún gate —cualquiera puede pedirla desde la
 *    pantalla de login— y sí una pieza más que desplegar.
 */
export async function reenviarAccesoTutor(email: string, redirectTo: string): Promise<RpcOutcome> {
  // Se comprueba el error: una escritura muda aquí diría «enviado» cuando no
  // ha salido nada, y dirección se quedaría esperando por una familia que no
  // recibe ningún correo.
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  if (error) return { ok: false, message: error.message };
  return { ok: true };
}

/** Un vínculo tal como lo pinta el panel: con el hijo resuelto y su final, si lo tuvo. */
export interface FamilyLink {
  id: string;
  student_id: string;
  student_name: string;
  relationship: string | null;
  is_primary: boolean;
  created_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
}

/** Un tutor con toda su familia colgando: los vínculos vivos y los retirados. */
export interface Family {
  guardian_id: string;
  /**
   * `profiles.id` de la cuenta del tutor, NULL si es declarativo. Hace falta
   * para poder desactivarla y borrarla: `guardian_id` no sirve — son tablas
   * distintas y la RPC de borrado pide el id del perfil.
   */
  profile_id: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  /** false = tutor declarativo: existe como ficha pero no puede entrar en la app. */
  hasAccount: boolean;
  /** `profiles.active` de esa cuenta. NULL cuando no hay cuenta que activar. */
  accountActive: boolean | null;
  links: FamilyLink[];
  revoked: FamilyLink[];
}

/**
 * Familias de TODOS los alumnos en una sola consulta. El listado de dirección
 * pinta cientos de filas: resolver la familia alumno por alumno serían N+1
 * viajes para un dato que se usa en una columna.
 */
export async function loadFamilies(): Promise<{
  familias: Map<string, FamilyOf>;
  error: string | null;
}> {
  const { data, error } = await supabase
    .from('guardian_students')
    .select('student_id, guardian:guardians!guardian_id(id, full_name, email, profile_id)')
    .is('revoked_at', null);

  // Un fallo de lectura dejaba el mapa vacío, y el listado de dirección pinta
  // entonces «sin familia» en TODAS las filas. Eso no es un hueco: es una
  // afirmación sobre quién tiene acceso a cada niño, que es justo lo que esta
  // columna existe para responder.
  if (error) return { familias: new Map(), error: error.message || 'Error desconocido' };

  const rows = (data ?? []).map((r) => ({
    student_id: r.student_id,
    guardian: one(r.guardian),
  }));

  // guardian_id → alumnos que cuelgan de él. De ahí salen los hermanos.
  const byGuardian = new Map<string, string[]>();
  for (const r of rows) {
    if (!r.guardian) continue;
    const list = byGuardian.get(r.guardian.id) ?? [];
    list.push(r.student_id);
    byGuardian.set(r.guardian.id, list);
  }

  const out = new Map<string, FamilyOf>();
  for (const r of rows) {
    if (!r.guardian) continue;
    const entry = out.get(r.student_id) ?? { guardians: [], siblings: [] };
    entry.guardians.push({
      id: r.guardian.id,
      name: r.guardian.full_name,
      email: r.guardian.email,
      hasAccount: r.guardian.profile_id !== null,
    });
    for (const sid of byGuardian.get(r.guardian.id) ?? []) {
      if (sid !== r.student_id && !entry.siblings.some((s) => s.id === sid)) {
        entry.siblings.push({ id: sid, name: '' }); // el nombre lo pone quien pinta
      }
    }
    out.set(r.student_id, entry);
  }
  return { familias: out, error: null };
}

/**
 * Cómo está cada familia que declara un tutor. Es la MISMA función que usa la
 * conversión (mig 208), a propósito: si el panel contara por su cuenta, diría
 * "quedan 3" mientras la conversión piensa otra cosa.
 */
export async function familyShapes(): Promise<FamilyShapeRow[]> {
  const { data, error } = await supabase.rpc('admin_family_shapes');
  if (error) return [];
  return (data ?? []) as FamilyShapeRow[];
}

/**
 * El censo de familias: cada tutor con sus hijos, vivos y retirados.
 *
 * Los vínculos RETIRADOS vienen en la misma consulta y no en una aparte porque
 * son la mitad interesante del dato: la pregunta que el panel tiene que poder
 * contestar es "quién ha tenido acceso a este niño", no "quién lo tiene hoy".
 */
export async function listFamilies(): Promise<{ familias: Family[]; error: string | null }> {
  const { data, error } = await supabase
    .from('guardians')
    .select(
      // ⚠️ `cuenta:profiles!profile_id` lleva la FK nombrada a la fuerza:
      // `guardians` tiene DOS hacia `profiles` (`profile_id` y `created_by`), y
      // un embed sin desambiguar no devuelve filas — devuelve 300 PGRST201 y la
      // pantalla se queda vacía (§33, #496). El guard de CI lo comprueba.
      `id, profile_id, full_name, email, phone,
       cuenta:profiles!profile_id(id, active),
       guardian_students(
         id, student_id, relationship, is_primary, created_at, revoked_at, revoked_reason,
         student:profiles!student_id(id, full_name)
       )`
    )
    // [356] Las fichas MUERTAS se van del censo. `anonymized_at` lo pone quien
    // mata la ficha —`anonymize_family` y el trigger de la 334—, así que esto
    // no adivina nada: lee la marca. Sin el filtro, el panel enseña para
    // siempre una familia «Usuario eliminado» con un vínculo revocado a un
    // alumno «Usuario eliminado»: una fila que no contesta nada. Medido en
    // produccion antes y despues de la 356.
    //
    // ⚠️ Y LO QUE ESTA PAGINA DEJA DE PODER CONTESTAR, dicho aqui porque es el
    // precio: de una familia que se fue ya no dice «quien tuvo acceso a este
    // niño». Eso vive en `audit_log` —que desde la 356 cuenta las filas de
    // familia— y **sin nombres**: la fila del borrado guarda uuids, cuentas y
    // fechas (medido: 0 apariciones de nombre o correo en todo audit_log).
    .is('anonymized_at', null)
    .order('full_name');

  // El censo de familias responde «quién ha tenido acceso a este niño». Una
  // lista vacía por un fallo de lectura contesta «nadie», que es la respuesta
  // más peligrosa que puede dar esta pantalla.
  if (error) return { familias: [], error: error.message || 'Error desconocido' };

  type LinkEmbed = {
    id: string;
    student_id: string;
    relationship: string | null;
    is_primary: boolean;
    created_at: string;
    revoked_at: string | null;
    revoked_reason: string | null;
    student: { id: string; full_name: string } | { id: string; full_name: string }[] | null;
  };
  type CuentaEmbed = { id: string; active: boolean } | { id: string; active: boolean }[] | null;
  type GuardianEmbed = Guardian & {
    guardian_students: LinkEmbed[] | null;
    cuenta: CuentaEmbed;
  };

  const familias = ((data ?? []) as unknown as GuardianEmbed[]).map((g) => {
    const all: FamilyLink[] = (g.guardian_students ?? []).map((l) => ({
      id: l.id,
      student_id: l.student_id,
      // Un hijo cuyo perfil ya no está (tombstone RGPD, mig 184) deja el embed
      // en null. Se pinta el hueco en vez de tumbar la fila entera: el vínculo
      // sigue siendo parte de la historia de acceso.
      student_name: one(l.student)?.full_name ?? '—',
      relationship: l.relationship,
      is_primary: l.is_primary,
      created_at: l.created_at,
      revoked_at: l.revoked_at,
      revoked_reason: l.revoked_reason,
    }));
    const cuenta = one(g.cuenta);
    return {
      guardian_id: g.id,
      profile_id: g.profile_id,
      full_name: g.full_name,
      email: g.email,
      phone: g.phone,
      hasAccount: g.profile_id !== null,
      // Si hay `profile_id` pero el embed no trae fila, la cuenta ya no existe:
      // se trata como "sin cuenta" en vez de inventarle un estado.
      accountActive: cuenta ? cuenta.active : null,
      links: all.filter((l) => l.revoked_at === null),
      revoked: all
        .filter((l) => l.revoked_at !== null)
        .sort((a, b) => (b.revoked_at ?? '').localeCompare(a.revoked_at ?? '')),
    };
  });
  return { familias, error: null };
}

/**
 * Cierra la rama `tutor_sin_cuenta`: crea la cuenta del tutor, la vincula y le
 * manda el enlace para definir contraseña.
 *
 * ⚠️ La regla de si esa rama aplica vive en SQL (`attach_tutor_account`
 * comprueba la forma contra `family_shapes`), no aquí ni en la EF. Duplicarla
 * en el cliente sería tener dos verdades sobre cuándo se puede vincular a un
 * menor, y la del cliente es la que se puede saltar.
 */
export async function inviteTutor(
  studentId: string,
  relationship: string | null
): Promise<RpcOutcome> {
  const { data, error } = await supabase.functions.invoke('invite-tutor', {
    // El destino del enlace NO se manda desde aquí: la EF lo lee de su propio
    // secret y se niega a seguir si no está configurado. Un cliente que
    // eligiera a dónde apunta el correo de "define tu contraseña" sería un
    // redirector abierto sobre una cuenta que aún no tiene dueño.
    body: { student_id: studentId, relationship: relationship || undefined },
  });

  if (error) {
    // El cuerpo JSON con nuestro { error } viaja en error.context (patrón de
    // DirectoresPage.handleReinvite).
    let raw = error.message;
    try {
      const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
      raw = (await ctx?.json?.())?.error ?? error.message;
    } catch {
      /* se queda el message del transporte */
    }
    return { ok: false, message: INVITE_ERRORS[raw] ?? raw };
  }
  if (data?.error) return { ok: false, message: INVITE_ERRORS[data.error] ?? data.error };

  // El vínculo YA está escrito aunque el correo no salga: la EF lo dice aparte
  // en vez de deshacerlo, así que el panel tiene que decirlo también.
  if (data?.mail_sent === false) {
    return { ok: true, message: 'Tutor vinculado, pero el correo no salió. Reenvíaselo.' };
  }
  return { ok: true };
}

const INVITE_ERRORS: Record<string, string> = {
  parent_data_incomplete: 'Falta el nombre o el email del tutor en la ficha del alumno.',
  email_already_registered: 'Ese email ya tiene cuenta: esta familia no es de esta rama.',
  not_applicable: 'La familia ya no está en esa forma. Recarga la página.',
  tutor_profile_exists: 'Ese tutor ya tiene perfil.',
  set_password_url_not_configured: 'El servidor no tiene configurado el destino del enlace.',
};

export async function listGuardians(): Promise<{ tutores: Guardian[]; error: string | null }> {
  const { data, error } = await supabase
    .from('guardians')
    .select('id, profile_id, full_name, email, phone')
    // [356] Una ficha muerta no puede poder elegirse como tutor de nadie: su
    // persona ejercio su derecho de supresion. El trigger de
    // `guardian_students` rechaza el vinculo igual —la garantia es el trigger,
    // no esto—, pero ofrecerla en un selector es ofrecer un error.
    .is('anonymized_at', null)
    .order('full_name');
  if (error) return { tutores: [], error: error.message || 'Error desconocido' };
  return { tutores: data ?? [], error: null };
}

export async function createGuardian(
  fullName: string,
  email: string | null,
  phone: string | null
): Promise<{ ok: boolean; id?: string; message?: string }> {
  const { data, error } = await supabase
    .from('guardians')
    .insert({
      full_name: fullName.trim(),
      email: email?.trim() || null,
      phone: phone?.trim() || null,
    })
    .select('id')
    .single();
  if (error || !data) return { ok: false, message: error?.message ?? 'No se pudo crear el tutor.' };
  return { ok: true, id: data.id };
}

/**
 * Vincula. No crea cuentas: dar login a un tutor es cosa de una Edge Function
 * (patrón `create-monitor`/`create-director`), no de una tabla.
 */
export async function linkStudent(
  guardianId: string,
  studentId: string,
  relationship: string | null,
  isPrimary = false
): Promise<RpcOutcome> {
  const { data, error } = await supabase.rpc('link_guardian_student', {
    p_guardian_id: guardianId,
    p_student_id: studentId,
    // El generador tipa los parámetros con DEFAULT como OPCIONALES (`?: string`),
    // no como nullables. Omitirlo y pasar NULL son lo mismo para la función.
    p_relationship: relationship ?? undefined,
    p_is_primary: isPrimary,
  });
  if (error) return { ok: false, message: error.message };
  const payload = asRecord(data);
  if (payload.ok === true) return { ok: true };
  return {
    ok: false,
    message:
      payload.error === 'already_linked'
        ? 'Ese tutor ya está vinculado a este alumno.'
        : 'No se pudo crear el vínculo.',
  };
}

/**
 * Retira un vínculo. No borra la fila: queda cuándo y quién. Hay que poder
 * demostrar cuándo terminó el acceso de alguien que lo tuvo.
 */
export async function revokeLink(linkId: string, reason?: string): Promise<RpcOutcome> {
  const { data, error } = await supabase.rpc('revoke_guardian_link', {
    p_link_id: linkId,
    p_reason: reason ?? undefined,
  });
  if (error) return { ok: false, message: error.message };
  const payload = asRecord(data);
  if (payload.ok === true) return { ok: true };
  return {
    ok: false,
    message:
      payload.error === 'already_revoked'
        ? 'Ese vínculo ya estaba retirado.'
        : 'No se pudo retirar el vínculo.',
  };
}

/** Vínculos vivos de un alumno, con el tutor resuelto. Para la ficha. */
export async function linksOfStudent(studentId: string): Promise<{
  vinculos: (GuardianLink & { guardian: Guardian | null })[];
  error: string | null;
}> {
  const { data, error } = await supabase
    .from('guardian_students')
    .select(
      'id, guardian_id, student_id, relationship, is_primary, revoked_at, guardian:guardians!guardian_id(id, profile_id, full_name, email, phone)'
    )
    .eq('student_id', studentId)
    .is('revoked_at', null);
  // «Este alumno no tiene ningún tutor vinculado» es una respuesta sobre quién
  // puede ver sus datos. No se da sin haberla leído.
  if (error) return { vinculos: [], error: error.message || 'Error desconocido' };
  return { vinculos: (data ?? []).map((r) => ({ ...r, guardian: one(r.guardian) })), error: null };
}

/** Hermanos: los otros alumnos que cuelgan de los mismos tutores. */
export async function siblingsOfStudent(studentId: string): Promise<{
  hermanos: { id: string; full_name: string }[];
  error: string | null;
}> {
  // Dos consultas encadenadas: el error de la primera se propaga, porque sin
  // los vínculos la lista de hermanos saldría vacía por el motivo equivocado.
  const { vinculos, error: errorVinculos } = await linksOfStudent(studentId);
  if (errorVinculos) return { hermanos: [], error: errorVinculos };
  if (vinculos.length === 0) return { hermanos: [], error: null };

  const { data, error } = await supabase
    .from('guardian_students')
    .select('student_id, student:profiles!student_id(id, full_name)')
    .in(
      'guardian_id',
      vinculos.map((l) => l.guardian_id)
    )
    .is('revoked_at', null);
  if (error) return { hermanos: [], error: error.message || 'Error desconocido' };

  const seen = new Set<string>();
  const out: { id: string; full_name: string }[] = [];
  for (const r of data ?? []) {
    const student = one(r.student);
    if (r.student_id === studentId || seen.has(r.student_id) || !student) continue;
    seen.add(r.student_id);
    out.push({ id: r.student_id, full_name: student.full_name });
  }
  return { hermanos: out, error: null };
}

export interface TutorConCuenta {
  /** Su `profiles.id`: con lo que firma un mensaje. NO el `guardians.id`. */
  profile_id: string;
  /** Los hijos VIVOS. Vacío si se le revocaron todos, o si nunca tuvo. */
  student_ids: string[];
}

/**
 * Los tutores que tienen cuenta, con los hijos que aún les cuelgan.
 *
 * Es lo que le faltaba al buzón del club para no dejar a un tutor fuera.
 * `ComunicacionesPage` pedía `role IN ('alumno','monitor')`, así que un tutor
 * —`role = 'tutor'`— no entraba en la lista y **su mensaje al club no aparecía
 * en ninguna conversación**. No se notaba porque hasta la fase C el tutor ni
 * siquiera veía el hilo para contestar; con el build de la app en camino, sí.
 *
 * ⚠️ POR QUÉ NO VALE `family_key()` PARA UN TUTOR
 *
 * Porque `family_key` busca en `guardian_students` **por `student_id`**, y un
 * tutor no es alumno: cae al escalón 3 y se saca una clave propia. Medido en
 * producción:
 *
 *     family_key(Adidas) = family_key(Nike) = 2d940fc5…
 *     family_key(Nike Junior, su tutor)     = 1d9cab9c…
 *
 * Un singleton, que es exactamente lo que hay que evitar: el club vería una
 * conversación con «Nike Junior» al lado de otra con «Adidas y Nike», sin nada
 * que dijera que son la misma casa. El vínculo se resuelve por donde está
 * escrito —`guardians` + `guardian_students`— y el tutor hereda la clave de sus
 * hijos.
 *
 * `profile_id IS NULL` queda fuera: un tutor sin cuenta no puede escribir, así
 * que no hay ningún mensaje suyo que colocar.
 */
export async function tutoresConCuenta(): Promise<TutorConCuenta[]> {
  const { data, error } = await supabase
    .from('guardians')
    .select('profile_id, guardian_students(student_id, revoked_at)')
    .not('profile_id', 'is', null);

  // Se propaga en vez de devolver una lista vacía: sin esto, un fallo de
  // lectura se leería como "no hay tutores" y el tutor volvería a quedarse
  // fuera del buzón — el mismo bug, ahora en silencio. Quien llama lo dice.
  if (error) throw error;

  type Fila = {
    profile_id: string | null;
    guardian_students: { student_id: string; revoked_at: string | null }[] | null;
  };

  const porCuenta = new Map<string, Set<string>>();
  for (const g of (data ?? []) as unknown as Fila[]) {
    if (!g.profile_id) continue;
    // Un mismo tutor puede tener más de una fila en `guardians` (dos altas, un
    // vínculo cada una). Se acumulan: sus hijos son la unión, no los de la
    // última fila que llegue.
    const hijos = porCuenta.get(g.profile_id) ?? new Set<string>();
    for (const l of g.guardian_students ?? []) {
      if (l.revoked_at === null) hijos.add(l.student_id);
    }
    porCuenta.set(g.profile_id, hijos);
  }
  return [...porCuenta].map(([profile_id, hijos]) => ({
    profile_id,
    student_ids: [...hijos],
  }));
}

// ============================================================
// BUG 14 · la cuenta de un tutor se desactiva y se borra desde aquí
// ============================================================
// Hasta ahora no había forma. `AlumnosPage` —la única pantalla con el modal de
// borrado— filtra `.eq('role','alumno')`, así que un tutor no aparecía en
// ninguna lista con acción. Medido: una cuenta que dirección creía borrada
// siguió entrando seis días.
//
// Las dos escrituras comprueban el RECUENTO, no solo el error. PostgREST
// responde 204 a un UPDATE que no toca ninguna fila: `error === null` no prueba
// nada (§ escrituras mudas). Medido con los claims del admin contra producción
// en un ensayo con ROLLBACK: la RLS deja las dos, 1 fila cada una.

export interface TutorInvisible {
  id: string;
  full_name: string;
  email: string | null;
  active: boolean;
}

/**
 * Cuentas con `role = 'tutor'` que NO tienen ninguna fila en `guardians`.
 *
 * ⚠️ Son las que esta página no puede pintar, porque la lista de familias sale
 * de `guardians`. Hoy no hay ninguna (medido: los 2 tutores tienen su fila),
 * pero si un alta futura deja una suelta, esa cuenta vuelve a ser invisible —
 * que es exactamente cómo el BUG 14 duró seis días. La página las saca en un
 * aviso en vez de callárselas.
 *
 * Los errores se PROPAGAN: una lectura fallida que devolviera [] se leería como
 * "no hay ninguna suelta", que es la conclusión contraria a la verdad.
 */
export async function tutoresInvisibles(): Promise<TutorInvisible[]> {
  const [perfiles, filas] = await Promise.all([
    // [356] `.is('deleted_at', null)`: UN TOMBSTONE NO ES UN TUTOR INVISIBLE,
    // es una persona borrada. Sin esto, cada borrado deja en el panel un aviso
    // ROJO permanente —«1 cuenta de tutor sin ficha de familia»— sobre alguien
    // que pidio irse, y encima invita a buscar una ficha que ya no existe.
    // Medido: con la 356 aplicada el aviso sobrevivia entero.
    supabase
      .from('profiles')
      .select('id, full_name, email, active')
      .eq('role', 'tutor')
      .is('deleted_at', null),
    supabase.from('guardians').select('profile_id').not('profile_id', 'is', null),
  ]);
  if (perfiles.error) throw perfiles.error;
  if (filas.error) throw filas.error;

  const conFila = new Set(
    ((filas.data ?? []) as { profile_id: string | null }[])
      .map((f) => f.profile_id)
      .filter((id): id is string => id !== null)
  );
  return ((perfiles.data ?? []) as TutorInvisible[]).filter((t) => !conFila.has(t.id));
}

/**
 * Activa o desactiva la cuenta de un tutor.
 *
 * No es un paso opcional antes de borrar: `admin_delete_account` exige
 * `active = false` y responde `22023 · Hay que desactivar la cuenta antes de
 * poder eliminarla`. Sin esto, el botón de borrar reproduciría el BUG 14 en un
 * sitio nuevo.
 *
 * Desactivar además le cierra la sesión: el trigger `trg_profiles_revoke_sessions`
 * revoca sus sesiones de auth. Es lo que no pasó durante seis días.
 */
export async function setCuentaTutorActiva(
  profileId: string,
  active: boolean
): Promise<RpcOutcome> {
  const { data, error } = await supabase
    .from('profiles')
    .update({ active })
    .eq('id', profileId)
    .select('id');

  if (error) return { ok: false, message: error.message };
  if ((data ?? []).length !== 1) {
    return {
      ok: false,
      message: 'El servidor no confirmó el cambio: la cuenta puede seguir como estaba.',
    };
  }
  return { ok: true };
}

/**
 * Borra la fila de `guardians` de un tutor cuya cuenta ya no existe, SOLO si no
 * le queda ningún vínculo — ni vivo ni retirado.
 *
 * Decisión de Jose: con hijos detrás, la fila se queda. `guardians.profile_id`
 * es `ON DELETE SET NULL`, así que al borrar la cuenta esa familia pasa a
 * «Tutor sin cuenta» y conserva la historia de quién tuvo acceso a cada menor,
 * que es la pregunta que esta página existe para contestar. Sin vínculos no hay
 * historia que conservar: es una ficha colgando de nadie.
 *
 * ⚠️ El `WHERE` repite la condición en SQL en vez de fiarse de la comprobación
 * del cliente. La RLS de `guardians` es `ALL: is_admin()` y deja borrar una fila
 * CON vínculos —medido—, arrastrando `guardian_students` por CASCADE. La regla
 * es nuestra, así que se escribe donde no se puede saltar por accidente.
 */
export async function borrarFilaGuardianSinVinculos(guardianId: string): Promise<RpcOutcome> {
  const { data: vinculos, error: errVinc } = await supabase
    .from('guardian_students')
    .select('id')
    .eq('guardian_id', guardianId);

  if (errVinc) return { ok: false, message: errVinc.message };
  if ((vinculos ?? []).length > 0) {
    return { ok: false, message: 'La ficha de tutor tiene vínculos: se conserva.' };
  }

  const { data, error } = await supabase
    .from('guardians')
    .delete()
    .eq('id', guardianId)
    .select('id');

  if (error) return { ok: false, message: error.message };
  if ((data ?? []).length !== 1) {
    return { ok: false, message: 'El servidor no confirmó el borrado de la ficha de tutor.' };
  }
  return { ok: true };
}
